import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Trash, Plus, PencilSimple, SignOut } from "@phosphor-icons/react";
import { api, auth } from "../../lib/api";
import { toast } from "sonner";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_1cfd506a-8d58-4dee-aebc-bac5c6ae0bd8/artifacts/olw9pyi7_1630458636004.jpg";

const SECTOR_OPTIONS = [
  { v: "clean_tech", l: "Clean Tech" },
  { v: "biogas", l: "Biogas & Biomass" },
  { v: "recycling", l: "Recycling" },
  { v: "natural_resources", l: "Natural Resources" },
];

const emptyPortfolio = {
  name: "",
  sector: "clean_tech",
  description_en: "",
  description_de: "",
  country: "",
  year_invested: "",
  website: "",
  image_url: "",
  status: "active",
  order: 0,
};

const emptyNews = {
  title_en: "",
  title_de: "",
  excerpt_en: "",
  excerpt_de: "",
  body_en: "",
  body_de: "",
  category: "News",
  image_url: "",
  published: true,
};

export default function AdminDashboard() {
  const [tab, setTab] = useState("portfolio");
  const [portfolio, setPortfolio] = useState([]);
  const [news, setNews] = useState([]);
  const [editing, setEditing] = useState(null); // { type, item }

  const load = async () => {
    try {
      const [p, n] = await Promise.all([
        api.get("/admin/portfolio"),
        api.get("/admin/news"),
      ]);
      setPortfolio(p.data || []);
      setNews(n.data || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        auth.logout();
        window.location.href = "/admin/login";
      }
    }
  };
  useEffect(() => {
    if (auth.isAuthed()) load();
  }, []);

  if (!auth.isAuthed()) return <Navigate to="/admin/login" replace />;

  const logout = () => {
    auth.logout();
    window.location.href = "/admin/login";
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-30 bg-white border-b border-[#EBEAE0]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="" className="h-7 w-7 object-contain" />
            <span className="font-display text-base tracking-tight">
              Elephant <span className="text-[#A6192E]">Equity</span>
              <span className="text-[#7A7D7A] text-xs ml-2 font-mono">/ admin</span>
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-xs text-[#7A7D7A] hidden sm:inline">{auth.email()}</span>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 text-sm text-[#A6192E] link-underline"
              data-testid="admin-logout"
            >
              <SignOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="flex items-center gap-1 border-b border-[#EBEAE0] mb-8">
          {[
            { id: "portfolio", label: `Portfolio (${portfolio.length})` },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`px-4 py-3 text-sm tracking-wide -mb-px border-b-2 transition-colors ${
                tab === t.id
                  ? "border-[#A6192E] text-[#A6192E]"
                  : "border-transparent text-[#4A4D4A] hover:text-[#1A1A1A]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "portfolio" && (
          <PortfolioPanel
            items={portfolio}
            reload={load}
            onEdit={(item) => setEditing({ type: "portfolio", item })}
            onCreate={() => setEditing({ type: "portfolio", item: { ...emptyPortfolio } })}
          />
        )}
      </main>

      {editing && (
        <EditorDrawer
          editing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function PortfolioPanel({ items, reload, onEdit, onCreate }) {
  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await api.delete(`/admin/portfolio/${id}`);
    toast.success("Deleted");
    reload();
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl tracking-tight">Portfolio companies</h2>
        <button
          onClick={onCreate}
          data-testid="admin-portfolio-create"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#A6192E] hover:bg-[#8C1527] text-white text-sm rounded-sm transition-colors"
        >
          <Plus size={14} weight="bold" /> New company
        </button>
      </div>
      <div className="bg-white border border-[#EBEAE0] rounded-sm overflow-hidden">
        <div className="grid grid-cols-12 px-5 py-3 bg-[#F3F2EB] text-[10px] uppercase tracking-[0.18em] text-[#4A4D4A]">
          <div className="col-span-4">Name</div>
          <div className="col-span-3">Sector</div>
          <div className="col-span-2">Country</div>
          <div className="col-span-1">Year</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {items.length === 0 && (
          <div className="px-5 py-10 text-sm text-[#7A7D7A]">No portfolio entries yet.</div>
        )}
        {items.map((p) => (
          <div
            key={p.id}
            className="grid grid-cols-12 px-5 py-4 border-t border-[#EBEAE0] items-center text-sm"
          >
            <div className="col-span-4 font-display text-base">{p.name}</div>
            <div className="col-span-3 text-[#4A4D4A]">
              {SECTOR_OPTIONS.find((s) => s.v === p.sector)?.l || p.sector}
            </div>
            <div className="col-span-2 text-[#4A4D4A]">{p.country || "—"}</div>
            <div className="col-span-1 font-mono text-xs text-[#4A4D4A]">
              {p.year_invested || "—"}
            </div>
            <div className="col-span-1 text-xs">
              <span
                className={`px-2 py-0.5 rounded-sm border ${
                  p.status === "active"
                    ? "border-[#1A4331]/30 text-[#1A4331]"
                    : "border-[#A6192E]/30 text-[#A6192E]"
                }`}
              >
                {p.status}
              </span>
            </div>
            <div className="col-span-1 flex justify-end gap-2">
              <button
                onClick={() => onEdit(p)}
                className="p-2 text-[#4A4D4A] hover:text-[#A6192E]"
                data-testid={`edit-portfolio-${p.id}`}
              >
                <PencilSimple size={16} />
              </button>
              <button
                onClick={() => remove(p.id)}
                className="p-2 text-[#4A4D4A] hover:text-[#A6192E]"
                data-testid={`delete-portfolio-${p.id}`}
              >
                <Trash size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NewsPanel({ items, reload, onEdit, onCreate }) {
  const remove = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await api.delete(`/admin/news/${id}`);
    toast.success("Deleted");
    reload();
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl tracking-tight">News & insights</h2>
        <button
          onClick={onCreate}
          data-testid="admin-news-create"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#A6192E] hover:bg-[#8C1527] text-white text-sm rounded-sm transition-colors"
        >
          <Plus size={14} weight="bold" /> New article
        </button>
      </div>
      <div className="bg-white border border-[#EBEAE0] rounded-sm overflow-hidden">
        <div className="grid grid-cols-12 px-5 py-3 bg-[#F3F2EB] text-[10px] uppercase tracking-[0.18em] text-[#4A4D4A]">
          <div className="col-span-6">Title</div>
          <div className="col-span-2">Category</div>
          <div className="col-span-2">Date</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {items.length === 0 && (
          <div className="px-5 py-10 text-sm text-[#7A7D7A]">No news entries yet.</div>
        )}
        {items.map((n) => (
          <div
            key={n.id}
            className="grid grid-cols-12 px-5 py-4 border-t border-[#EBEAE0] items-center text-sm"
          >
            <div className="col-span-6 font-display text-base">{n.title_en}</div>
            <div className="col-span-2 text-[#4A4D4A]">{n.category}</div>
            <div className="col-span-2 font-mono text-xs text-[#4A4D4A]">
              {new Date(n.published_at).toLocaleDateString("en-GB")}
            </div>
            <div className="col-span-1 text-xs">
              <span
                className={`px-2 py-0.5 rounded-sm border ${
                  n.published
                    ? "border-[#1A4331]/30 text-[#1A4331]"
                    : "border-[#7A7D7A]/30 text-[#7A7D7A]"
                }`}
              >
                {n.published ? "live" : "draft"}
              </span>
            </div>
            <div className="col-span-1 flex justify-end gap-2">
              <button
                onClick={() => onEdit(n)}
                className="p-2 text-[#4A4D4A] hover:text-[#A6192E]"
                data-testid={`edit-news-${n.id}`}
              >
                <PencilSimple size={16} />
              </button>
              <button
                onClick={() => remove(n.id)}
                className="p-2 text-[#4A4D4A] hover:text-[#A6192E]"
                data-testid={`delete-news-${n.id}`}
              >
                <Trash size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EditorDrawer({ editing, onClose, onSaved }) {
  const isPortfolio = editing.type === "portfolio";
  const [form, setForm] = useState(editing.item);
  const [saving, setSaving] = useState(false);
  const isNew = !form.id;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const path = isPortfolio ? "/admin/portfolio" : "/admin/news";
      const payload = { ...form };
      if (isPortfolio) {
        payload.year_invested = payload.year_invested
          ? parseInt(payload.year_invested, 10)
          : null;
        payload.order = payload.order ? parseInt(payload.order, 10) : 0;
      }
      // remove non-payload fields
      delete payload.id;
      delete payload.created_at;
      delete payload.published_at;

      if (isNew) {
        await api.post(path, payload);
      } else {
        await api.patch(`${path}/${form.id}`, payload);
      }
      toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="editor-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <form
        onSubmit={save}
        className="w-full max-w-2xl bg-white h-full overflow-y-auto p-8"
      >
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="text-xs tracking-[0.22em] uppercase text-[#A6192E]">
              {isPortfolio ? "Portfolio" : "News"}
            </div>
            <h2 className="font-display text-2xl tracking-tight mt-1">
              {isNew ? "Create new" : "Edit"}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-[#4A4D4A] link-underline"
          >
            Close
          </button>
        </div>

        {isPortfolio ? (
          <div className="space-y-5">
            <Field label="Name" value={form.name} onChange={(v) => setField("name", v)} required />
            <SelectField
              label="Sector"
              value={form.sector}
              onChange={(v) => setField("sector", v)}
              options={SECTOR_OPTIONS}
            />
            <Textarea
              label="Description (EN)"
              value={form.description_en}
              onChange={(v) => setField("description_en", v)}
              required
            />
            <Textarea
              label="Description (DE)"
              value={form.description_de}
              onChange={(v) => setField("description_de", v)}
            />
            <div className="grid grid-cols-3 gap-4">
              <Field
                label="Country"
                value={form.country}
                onChange={(v) => setField("country", v)}
              />
              <Field
                label="Year invested"
                value={form.year_invested || ""}
                onChange={(v) => setField("year_invested", v)}
              />
              <SelectField
                label="Status"
                value={form.status}
                onChange={(v) => setField("status", v)}
                options={[
                  { v: "active", l: "Active" },
                  { v: "exited", l: "Exited" },
                ]}
              />
            </div>
            <Field
              label="Website"
              value={form.website}
              onChange={(v) => setField("website", v)}
            />
            <Field
              label="Image URL"
              value={form.image_url}
              onChange={(v) => setField("image_url", v)}
            />
            <Field
              label="Order"
              value={form.order ?? 0}
              onChange={(v) => setField("order", v)}
            />
          </div>
        ) : (
          <div className="space-y-5">
            <Field label="Title (EN)" value={form.title_en} onChange={(v) => setField("title_en", v)} required />
            <Field label="Title (DE)" value={form.title_de} onChange={(v) => setField("title_de", v)} />
            <Textarea
              label="Excerpt (EN)"
              value={form.excerpt_en}
              onChange={(v) => setField("excerpt_en", v)}
              required
            />
            <Textarea
              label="Excerpt (DE)"
              value={form.excerpt_de}
              onChange={(v) => setField("excerpt_de", v)}
            />
            <Textarea
              label="Body (EN)"
              value={form.body_en}
              onChange={(v) => setField("body_en", v)}
              rows={6}
            />
            <Textarea
              label="Body (DE)"
              value={form.body_de}
              onChange={(v) => setField("body_de", v)}
              rows={6}
            />
            <div className="grid grid-cols-2 gap-4">
              <Field
                label="Category"
                value={form.category}
                onChange={(v) => setField("category", v)}
              />
              <SelectField
                label="Status"
                value={form.published ? "true" : "false"}
                onChange={(v) => setField("published", v === "true")}
                options={[
                  { v: "true", l: "Published" },
                  { v: "false", l: "Draft" },
                ]}
              />
            </div>
            <Field
              label="Image URL"
              value={form.image_url}
              onChange={(v) => setField("image_url", v)}
            />
          </div>
        )}

        <div className="flex justify-end gap-3 mt-10">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-3 border border-[#1A1A1A]/15 rounded-sm text-sm hover:bg-[#F3F2EB]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="editor-save"
            onClick={save}
            className="px-6 py-3 bg-[#A6192E] hover:bg-[#8C1527] text-white text-sm rounded-sm transition-colors disabled:opacity-60"
          >
            {saving ? "Saving…" : isNew ? "Create" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, required, type = "text" }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.18em] text-[#4A4D4A]">{label}</label>
      <input
        type={type}
        required={required}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full px-4 py-3 bg-[#FAF9F6] border border-[#EBEAE0] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#A6192E]/30 focus:border-[#A6192E]"
      />
    </div>
  );
}

function Textarea({ label, value, onChange, required, rows = 3 }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.18em] text-[#4A4D4A]">{label}</label>
      <textarea
        required={required}
        rows={rows}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full px-4 py-3 bg-[#FAF9F6] border border-[#EBEAE0] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#A6192E]/30 focus:border-[#A6192E] resize-none"
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.18em] text-[#4A4D4A]">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full px-4 py-3 bg-[#FAF9F6] border border-[#EBEAE0] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#A6192E]/30 focus:border-[#A6192E]"
      >
        {options.map((o) => (
          <option key={o.v} value={o.v}>
            {o.l}
          </option>
        ))}
      </select>
    </div>
  );
}
